// Who "owns" a room's scenes.
//
// THE PROBLEM THIS SOLVES
// Scenes ride on the Wall Mode tables, and every wall endpoint is keyed by
// UserId (`GetWallPanelsByUserId` — there is no by-room variant). So a scene was
// effectively owned by whoever created it: two users sharing a room each got
// their OWN hidden `__scene__<RoomID>` panel and could not see the other's.
//
// A scene belongs to the ROOM, not to a person. So instead of reading and
// writing as the logged-in user, we read and write as the room's owning
// account — the Location's MasterUserId. Everyone with access to that location
// then resolves to the same panel and sees the same scenes.
//
// No backend change is needed for this. (A proper `GetWallPanelsByRoomId` would
// be cleaner, since scene rows now carry a UserId who may not have created them,
// but the deployed API has no such endpoint.)
//
// Editing rights follow the same value: only the owner may add / change / delete.
// Everyone else sees the scenes and can run them.

import { getLocationDetail } from '../api/api';

// LocationID -> MasterUserId. A location's owner does not change during a
// session, and this is consulted on every scenes load, so cache it.
const ownerCache = new Map();

export async function fetchLocationOwnerId(locationId) {
  if (locationId == null || locationId === '') return null;
  const key = String(locationId);
  if (ownerCache.has(key)) return ownerCache.get(key);
  try {
    const res = await getLocationDetail(locationId);
    const row = Array.isArray(res.data) ? res.data[0] : res.data;
    const owner = row && row.MasterUserId != null ? Number(row.MasterUserId) : null;
    if (owner != null && !Number.isNaN(owner)) {
      ownerCache.set(key, owner);
      return owner;
    }
  } catch (e) { /* fall through */ }
  return null;
}

export function clearOwnerCache() {
  ownerCache.clear();
}

// The account a room's scenes are stored under.
//
// Falls back to the logged-in user when the owner can't be resolved (no
// locationId to hand, or the lookup failed). That fallback is deliberate: it is
// exactly the old per-user behaviour, so a hiccup degrades to "you see your own
// scenes" rather than to a blank screen or, worse, writing someone else's panel.
export async function resolveSceneOwner(userId, locationId) {
  const owner = await fetchLocationOwnerId(locationId);
  return owner != null ? owner : Number(userId);
}
